import { isFeatureFlagEnabled } from '@repo/feature-flag';
import { QrCodeMirroredIcon, ChevronRightIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import classNames from 'classnames';
import { useCallback } from 'react';
import { Outlet, useMatch } from 'react-router-dom';
import { Badge, Button } from '@repo/ui';

import { ControlButtonGroup } from '@features/parkingMap';

import Dialog from '@shared/components/Dialog';
import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

const DISCOUNT_BANNER_HEIGHT = 36; // in px - visible height of banner with discount

interface Props {
  testId: TestId;
  onDrawerHeightChange?: (height: number) => void;
  onOpenDiscountDrawer: () => void;
  onToggleQr: () => void;
}

export default function MapLayoutDialog({ testId, onDrawerHeightChange, onOpenDiscountDrawer, onToggleQr }: Props) {
  const parkingSlug = useParkingSearchStore((state) => state.selectedParking.slug);
  const selectionNonce = useParkingSearchStore((state) => state.selectionNonce);
  const isMapRoute = Boolean(useMatch(ROUTE_PATHS[RouteName.MAP]));
  const isParkingSearchResultsRoute = Boolean(useMatch(ROUTE_PATHS[RouteName.PARKING_SEARCH_RESULTS]));
  const isParkingDetailsRoute = Boolean(useMatch(ROUTE_PATHS[RouteName.PARKING_DETAILS]));
  const isDiscountBannerEnabled = isFeatureFlagEnabled('parkingDiscountBanner');
  const mapMoveNonce = useParkingSearchStore((state) => state.mapMoveNonce);
  const isDiscountBannerVisible = isParkingSearchResultsRoute && isDiscountBannerEnabled;
  const expandTrigger = isMapRoute ? 'map' : isParkingDetailsRoute ? `${parkingSlug}:${selectionNonce}` : undefined;

  const handleDrawerHeightChange = useCallback(
    (drawerContentHeight: number) => {
      const extraHeight = isDiscountBannerVisible ? DISCOUNT_BANNER_HEIGHT : 0;
      onDrawerHeightChange?.(drawerContentHeight + extraHeight);
    },
    [onDrawerHeightChange, isDiscountBannerVisible]
  );

  return (
    <Dialog
      contentClassName="px-0"
      collapseTrigger={mapMoveNonce}
      expandTrigger={expandTrigger}
      onHeightChange={handleDrawerHeightChange}
      sticker={
        <div className="relative">
          <div className="absolute -bottom-4 flex w-full">
            <div className="w-full">
              {isParkingSearchResultsRoute && isDiscountBannerEnabled && (
                <button
                  onClick={onOpenDiscountDrawer}
                  className="bg-success-strong flex w-full cursor-pointer items-center justify-between gap-2 rounded-t-lg px-4 pt-1.5 pb-6 text-left"
                >
                  <div className="flex items-center gap-2">
                    <Badge
                      label={tr('parking_search:discount_applied.badge')}
                      size="sm"
                      circle
                      className="text-fg-success-strong bg-success-medium border-0 px-2"
                      testId={testId.append('discountBadge')}
                    />
                    <span className="text-sm text-white">{tr('parking_search:discount_applied.text')}</span>
                  </div>
                  <ChevronRightIcon className="text-white" size={16} />
                </button>
              )}
            </div>
            <div
              className={classNames(
                'absolute right-4 flex -translate-y-full flex-col pb-5 md:right-auto md:left-4',
                isParkingSearchResultsRoute && isDiscountBannerEnabled ? 'top-0' : '-top-4'
              )}
            >
              <ControlButtonGroup testId={testId.append('mapControl')} />
            </div>
          </div>

          <div className="pointer-events-none absolute flex w-full -translate-y-full items-center justify-center">
            <div className="pointer-events-auto pb-3">
              {isMapRoute && (
                <Button leftIcon={QrCodeMirroredIcon} onClick={onToggleQr} testId={testId.append('scanQrButton')}>
                  {tr('parking_qr_code:scan.button')}
                </Button>
              )}
            </div>
          </div>
        </div>
      }
      testId={testId.append('drawer')}
    >
      <div className="flex h-full w-full flex-col overflow-hidden">
        <Outlet />
      </div>
    </Dialog>
  );
}
